import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import dayjs from "dayjs";
import { UserContext } from "../../context/UserContext";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReceiptIcon from "@mui/icons-material/Receipt";
import CallIcon from "@mui/icons-material/Call";
import EmailIcon from "@mui/icons-material/Email";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import TanStackTable from "../Misc/Table/TanStackTable";
import { getAPICall, getBlobAPICall, putAPICall, postAPICall, deleteAPICall } from "../../config/apiCalls";
import "./ContactsCleanupTab.css";

// Same debounce pattern used in PostAmendment.js's HubSpot deal/property pickers.
function debounce(callback, wait) {
	let timeoutId = null;
	return (...args) => {
		window.clearTimeout(timeoutId);
		timeoutId = window.setTimeout(() => { callback(...args); }, wait);
	};
}

// The 5 association types a contact can be linked to. Each maps 1:1 to a
// HubSpot object type (see ASSOCIATION_GROUPS in phaseReportsService.js and
// ASSOCIATION_OBJECT_TYPES/ASSOCIATION_GROUP_CONFIG in the hubspot routes —
// the groupKey here must match those exactly). Search-as-you-type for each
// hits /api/hubspot/search/associationOptions?groupKey=<key>.
const ASSOC_GROUPS = [
	{ key: "deals", label: "Deals", placeholder: "Search deals…" },
	{ key: "companies", label: "Companies", placeholder: "Search companies…" },
	{ key: "entities", label: "Entities", placeholder: "Search entities…" },
	{ key: "properties", label: "Properties", placeholder: "Search properties…" },
	{ key: "units", label: "Units", placeholder: "Search units…" },
];

// Maps the /api/phaseReports/pmDashboardContacts response shape onto the shape
// the table/edit-panel already work with. Association fields keep their real
// {id, name} pairs (not flattened to plain strings) so edits can be diffed
// against the original set and turned into associate/disassociate calls on save.
function mapApiContact(c) {
	return {
		id: c.id,
		first: c.firstName || "",
		last: c.lastName || "",
		email: c.email || "",
		phone: c.phone || "",
		mobilePhone: c.mobilePhone || "",
		lastContactDate: c.lastContactDate || null,
		ownedByMe: !!c.ownedByMe,
		ignored: !!c.ignored,
		hubspotUrl: c.hubspotUrl || null,
		deals: c.deals || [],
		companies: c.companies || [],
		entities: c.entities || [],
		properties: c.properties || [],
		units: c.units || [],
	};
}

// ── Helpers ──────────────────────────────────────────────────────────────────

// Aircall auto-creates a HubSpot contact for unrecognized callers with the
// phone number as the first name and this literal last name — both fields
// are technically non-empty, but it's not a real name and needs cleanup
// same as a truly blank one.
function isAutoGeneratedName(c) {
	return /aircall new contact/i.test(c.last || "");
}

function hasRealName(c) {
	return !!(c.first && c.last) && !isAutoGeneratedName(c);
}

// "Needs attention" is purely about having a real name — missing email/
// associations are still surfaced (see missingFields, shown as a note under
// the name) but don't drive the attention flag/highlight/filter/sort.
function needsAttention(c) {
	return !hasRealName(c);
}

function missingFields(c) {
	const m = [];
	if (!hasRealName(c)) m.push("name");
	if (!c.email) m.push("email");
	if (!c.deals.length && !c.companies.length && !c.entities.length && !c.units.length && !c.properties.length) m.push("association");
	return m;
}

function displayName(c) {
	if (isAutoGeneratedName(c)) return "Unknown Caller";
	const n = [c.first, c.last].filter(Boolean).join(" ").trim();
	return n || "Unknown Caller";
}

// Table display: prefer mobile since that's the more likely direct-reach
// number, falling back to the office/other phone if mobile isn't set.
function primaryPhone(c) {
	return c.mobilePhone || c.phone || "—";
}

function lastContactTime(c) {
	if (!c.lastContactDate) return null;
	const t = new Date(c.lastContactDate).getTime();
	return Number.isNaN(t) ? null : t;
}

function formatLastContact(c) {
	const t = lastContactTime(c);
	return t === null ? "—" : dayjs(t).format("MMM D, YYYY");
}

const muted = (text) => <span style={{ color: "#9ca3af", fontStyle: "italic" }}>{text}</span>;

// Renders an association-array table cell: first item's name, "+N more" if
// there are others, or the muted fallback text if the array is empty.
function assocCell(items, emptyText = "—") {
	if (!items.length) return muted(emptyText);
	return (
		<div className="ccStack">
			<span>{items[0].name}</span>
			{items.length > 1 && <span className="ccMuted">+{items.length - 1} more</span>}
		</div>
	);
}

const FILTER_CHIPS = [
	{ key: "all", label: "All" },
	{ key: "attention", label: "Needs Attention" },
];

// Which side of the shared HubSpot `pm_dashboard_ignored` flag is shown. The
// filter chips above apply within whichever view is selected.
const VIEW_TOGGLES = [
	{ key: "active", label: "Active" },
	{ key: "ignored", label: "Ignored" },
];

// Mirrors the real toolbar + table layout with grayed-out placeholder blocks
// — same .pmSkeleton shimmer used by the dashboard's own SkeletonPage
// (PMProjectDashboard.css), reused here since it's already loaded globally.
const SKELETON_COLUMN_WIDTHS = [220, 160, 220, 130, 130, 130, 130, 130, 130, 110, 40, 40, 40, 44];

function ContactsSkeleton() {
	return (
		<>
			<div className="ccToolbar">
				<div className="pmSkeleton" style={{ width: 320, height: 36, borderRadius: 8 }} />
				<div className="ccChips">
					{[64, 150, 150].map((w, i) => (
						<div key={i} className="pmSkeleton" style={{ width: w, height: 30, borderRadius: 999 }} />
					))}
					<div className="pmSkeleton" style={{ width: 190, height: 30, borderRadius: 999 }} />
				</div>
			</div>
			<div className="ccTableWrap">
				<div className="ccSkeletonTable">
					<div className="ccSkeletonRow ccSkeletonRow--header">
						{SKELETON_COLUMN_WIDTHS.map((w, i) => (
							<div key={i} className="pmSkeleton" style={{ width: w, height: 12, borderRadius: 3 }} />
						))}
					</div>
					{Array.from({ length: 9 }).map((_, r) => (
						<div key={r} className="ccSkeletonRow">
							{SKELETON_COLUMN_WIDTHS.map((w, i) => (
								<div key={i} className="pmSkeleton" style={{ width: w, height: 14, borderRadius: 3 }} />
							))}
						</div>
					))}
				</div>
			</div>
		</>
	);
}

// ── Component ────────────────────────────────────────────────────────────────

export default function ContactsCleanupTab({ employee, bqeIds }) {
	const { instance, accounts } = useMsal();
	const loggedInId = useContext(UserContext);
	const [contacts, setContacts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [loadError, setLoadError] = useState("");
	const [query, setQuery] = useState("");
	const [filter, setFilter] = useState("all");
	const [view, setView] = useState("active");
	// Contact IDs with an ignore/restore PATCH in flight, so the row button
	// can't be double-clicked into conflicting writes.
	const [ignoreSaving, setIgnoreSaving] = useState({});
	const [ignoreError, setIgnoreError] = useState("");
	const [openId, setOpenId] = useState(null);
	const [savedNote, setSavedNote] = useState("");
	const [saving, setSaving] = useState(false);
	const [saveError, setSaveError] = useState("");
	// Snapshot of a contact's association arrays taken the moment its edit
	// dialog opens — diffed against the (locally-edited) live arrays on save
	// to know which associations to create/remove in HubSpot.
	const originalAssociationsRef = useRef(null);
	const [activityContact, setActivityContact] = useState(null);
	const [activity, setActivity] = useState([]);
	const [activityLoading, setActivityLoading] = useState(false);
	const [activityError, setActivityError] = useState("");
	// Per-activity-row state for the Recent Activity panel's inline deal
	// picker, keyed by `${type}:${id}` — set while handleActivityDealsChange
	// is diffing/applying a confirmed edit (see ActivityPanel's Save button).
	const [activityDealSaving, setActivityDealSaving] = useState({});
	const [activityDealError, setActivityDealError] = useState({});

	useEffect(() => {
		if (!activityContact) return;
		let cancelled = false;
		setActivityLoading(true);
		setActivityError("");
		getAPICall(instance, accounts[0], `/api/phaseReports/contactActivity?contactId=${encodeURIComponent(activityContact.id)}`)
			.then((res) => {
				if (cancelled) return;
				setActivity(res.data?.activity || []);
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("[ContactsCleanupTab] activity fetch failed", err);
				setActivityError("Failed to load activity from HubSpot.");
				setActivity([]);
			})
			.finally(() => { if (!cancelled) setActivityLoading(false); });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activityContact?.id]);

	useEffect(() => {
		if (!employee?.id) { setContacts([]); setLoading(false); return; }
		let cancelled = false;
		setLoading(true);
		setLoadError("");
		const params = new URLSearchParams({ employeeId: employee.id });
		if (bqeIds) params.set("bqeIds", bqeIds);
		const url = `/api/phaseReports/pmDashboardContacts?${params.toString()}`;
		getAPICall(instance, accounts[0], url)
			.then((res) => {
				if (cancelled) return;
				setContacts((res.data?.contacts || []).map(mapApiContact));
			})
			.catch((err) => {
				if (cancelled) return;
				console.error("[ContactsCleanupTab] contacts fetch failed", {
					status: err.response?.status,
					data: err.response?.data,
					message: err.message,
				});
				setLoadError("Failed to load contacts from HubSpot.");
				setContacts([]);
			})
			.finally(() => { if (!cancelled) setLoading(false); });
		return () => { cancelled = true; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [employee?.id, bqeIds]);

	const patchContact = (id, patch) => {
		setContacts((cs) => cs.map((c) => (c.id === id ? { ...c, ...patch } : c)));
	};

	// Compares each association group's live (edited) array against the
	// snapshot taken when the dialog opened, and issues one associate/
	// disassociate call per added/removed item.
	const syncContactAssociations = async (contact) => {
		const original = originalAssociationsRef.current || {};
		for (const group of ASSOC_GROUPS) {
			const before = original[group.key] || [];
			const after = contact[group.key] || [];
			const beforeIds = new Set(before.map((x) => x.id));
			const afterIds = new Set(after.map((x) => x.id));
			const toAdd = after.filter((x) => !beforeIds.has(x.id));
			const toRemove = before.filter((x) => !afterIds.has(x.id));
			for (const item of toAdd) {
				await putAPICall(instance, accounts[0], "/api/hubspot/associateContact", {
					contactId: contact.id, groupKey: group.key, toObjectId: item.id,
				});
			}
			for (const item of toRemove) {
				await deleteAPICall(instance, accounts[0], "/api/hubspot/associateContact", {
					contactId: contact.id, groupKey: group.key, toObjectId: item.id,
				});
			}
		}
	};

	// Diffs the activity row's (locally-edited) deal list against what's
	// committed and issues associate/disassociate calls for the difference —
	// only called when the Recent Activity panel's deal editor is confirmed,
	// not on every picker change. Returns true on success so the panel knows
	// it's safe to drop back to the read-only view; on failure the row stays
	// in edit mode with the error shown so the user can retry or cancel.
	const handleActivityDealsChange = async (item, newDeals) => {
		const key = `${item.type}:${item.id}`;
		const before = item.deals || [];
		const beforeIds = new Set(before.map((d) => d.id));
		const afterIds = new Set(newDeals.map((d) => d.id));
		const toAdd = newDeals.filter((d) => !beforeIds.has(d.id));
		const toRemove = before.filter((d) => !afterIds.has(d.id));
		if (!toAdd.length && !toRemove.length) return true;

		setActivityDealSaving((s) => ({ ...s, [key]: true }));
		setActivityDealError((e) => ({ ...e, [key]: "" }));
		try {
			const engagementType = item.type === "call" ? "calls" : "emails";
			for (const deal of toAdd) {
				await putAPICall(instance, accounts[0], "/api/hubspot/associateEngagementDeal", {
					engagementType, engagementId: item.id, dealId: deal.id,
				});
			}
			for (const deal of toRemove) {
				await deleteAPICall(instance, accounts[0], "/api/hubspot/associateEngagementDeal", {
					engagementType, engagementId: item.id, dealId: deal.id,
				});
			}
			setActivity((cur) => cur.map((a) => (a.type === item.type && a.id === item.id ? { ...a, deals: newDeals } : a)));
			return true;
		} catch (err) {
			console.error("[ContactsCleanupTab] activity deal association failed", err);
			setActivityDealError((e) => ({ ...e, [key]: "Failed to update deal association." }));
			return false;
		} finally {
			setActivityDealSaving((s) => ({ ...s, [key]: false }));
		}
	};

	// Best-effort — a logging failure should never mask (or block on top of)
	// the real save outcome, so this swallows its own errors.
	const logAutomation = async (success, message) => {
		try {
			await postAPICall(instance, accounts[0], "/api/logs/automation/add", {
				employeeID: loggedInId,
				date_time: dayjs().format("MMMM D, YYYY h:mm:ss A"),
				success,
				message,
				tool_name: "My Contacts",
			});
		} catch (err) {
			console.error("[ContactsCleanupTab] automation log write failed", err);
		}
	};

	const handleSaveContact = async (contact) => {
		setSaving(true);
		setSaveError("");
		setSavedNote("");
		const contactLabel = `${displayName(contact)} (${contact.id})`;
		try {
			await putAPICall(instance, accounts[0], "/api/hubspot/updateContact", {
				contactId: contact.id,
				firstname: contact.first,
				lastname: contact.last,
				email: contact.email,
				mobilephone: contact.mobilePhone.trim(),
				phone: contact.phone.trim(),
			});
			await syncContactAssociations(contact);
			originalAssociationsRef.current = ASSOC_GROUPS.reduce(
				(acc, group) => ({ ...acc, [group.key]: contact[group.key] }),
				{},
			);
			setSavedNote("Saved to HubSpot just now");
			setOpenId(null);
			logAutomation(true, `Updated HubSpot contact ${contactLabel} via My Contacts.`);
		} catch (err) {
			console.error("[ContactsCleanupTab] save failed", {
				status: err.response?.status,
				data: err.response?.data,
				message: err.message,
			});
			const errorMessage = err.response?.data?.message || err.message || "Unknown error";
			setSaveError(err.response?.data?.message || "Failed to save contact to HubSpot.");
			logAutomation(false, `Failed to update HubSpot contact ${contactLabel} via My Contacts: ${errorMessage}`);
		} finally {
			setSaving(false);
		}
	};

	// Flips the shared HubSpot `pm_dashboard_ignored` flag. Only the local copy
	// changes after HubSpot confirms, so the contact moves between the Active
	// and Ignored views only on a successful write.
	const handleToggleIgnored = async (contact) => {
		const nextIgnored = !contact.ignored;
		const contactLabel = `${displayName(contact)} (${contact.id})`;
		setIgnoreSaving((s) => ({ ...s, [contact.id]: true }));
		setIgnoreError("");
		try {
			await putAPICall(instance, accounts[0], "/api/hubspot/updateContact", {
				contactId: contact.id,
				pm_dashboard_ignored: nextIgnored ? "true" : "false",
			});
			patchContact(contact.id, { ignored: nextIgnored });
			logAutomation(true, `${nextIgnored ? "Ignored" : "Restored"} HubSpot contact ${contactLabel} via My Contacts.`);
		} catch (err) {
			console.error("[ContactsCleanupTab] ignore toggle failed", {
				status: err.response?.status,
				data: err.response?.data,
				message: err.message,
			});
			const errorMessage = err.response?.data?.message || err.message || "Unknown error";
			setIgnoreError(`Failed to ${nextIgnored ? "ignore" : "restore"} ${displayName(contact)} in HubSpot.`);
			logAutomation(false, `Failed to ${nextIgnored ? "ignore" : "restore"} HubSpot contact ${contactLabel} via My Contacts: ${errorMessage}`);
		} finally {
			setIgnoreSaving((s) => ({ ...s, [contact.id]: false }));
		}
	};

	const ignoredCount = useMemo(() => contacts.filter((c) => c.ignored).length, [contacts]);

	const viewContacts = useMemo(
		() => contacts.filter((c) => (view === "ignored" ? c.ignored : !c.ignored)),
		[contacts, view],
	);

	const attentionCount = useMemo(() => viewContacts.filter(needsAttention).length, [viewContacts]);

	const visibleContacts = useMemo(() => {
		const q = query.trim().toLowerCase();
		const list = viewContacts.filter((c) => {
			if (filter === "attention" && !needsAttention(c)) return false;
			if (!q) return true;
			const names = (arr) => arr.map((x) => x.name).join(" ");
			const hay = [displayName(c), c.email, c.phone, c.mobilePhone, names(c.deals), names(c.companies), names(c.entities), names(c.properties), names(c.units)].join(" ").toLowerCase();
			return hay.includes(q);
		});
		return [...list].sort((a, b) => {
			const attnDiff = Number(needsAttention(b)) - Number(needsAttention(a));
			if (attnDiff !== 0) return attnDiff;
			if (!needsAttention(a)) return 0;
			// Within the "needs attention" (unknown-name) group, most recently
			// contacted first; contacts with no last-contact date sink to the bottom.
			const at = lastContactTime(a);
			const bt = lastContactTime(b);
			if (at === null && bt === null) return 0;
			if (at === null) return 1;
			if (bt === null) return -1;
			return bt - at;
		});
	}, [viewContacts, query, filter]);

	const openContact = contacts.find((c) => c.id === openId) || null;

	const columns = useMemo(() => [
		{
			id: "name",
			accessorFn: (row) => displayName(row),
			header: () => <span>Name</span>,
			size: 220,
			cell: ({ row }) => {
				const c = row.original;
				const miss = missingFields(c);
				return (
					<div className="ccName">
						<span className={hasRealName(c) ? "ccName-primary" : "ccName-primary ccName-primary--empty"}>{displayName(c)}</span>
						{miss.length > 0 && (
							<span className="ccName-missing"><span className="ccName-missingDot" />Missing {miss.join(", ")}</span>
						)}
					</div>
				);
			},
		},
		{
			id: "phone",
			accessorFn: (row) => primaryPhone(row),
			header: () => <span>Phone</span>,
			size: 160,
			cell: ({ row }) => {
				const c = row.original;
				return (
					<span className="ccNowrap">
						{primaryPhone(c)}
						{c.phone && c.mobilePhone && <span className="ccMuted"> +1</span>}
					</span>
				);
			},
		},
		{
			accessorKey: "email",
			header: () => <span>Email</span>,
			size: 220,
			cell: ({ row }) => (row.original.email ? <span className="ccWrap">{row.original.email}</span> : muted("Not set")),
		},
		{
			id: "owner",
			accessorFn: (row) => (row.ownedByMe ? "Owned by You" : "On Your Deal"),
			header: () => <span>Owner</span>,
			size: 130,
			cell: ({ row }) => (
				<span className={`ccSourcePill ${row.original.ownedByMe ? "ccSourcePill--hubspot" : "ccSourcePill--neutral"}`}>
					{row.original.ownedByMe ? "Owned by You" : "On Your Deal"}
				</span>
			),
		},
		{
			id: "lastContact",
			accessorFn: (row) => lastContactTime(row) || 0,
			header: () => <span>Last Contact</span>,
			size: 130,
			cell: ({ row }) => {
				const t = lastContactTime(row.original);
				return t === null ? muted("—") : <span className="ccNowrap">{formatLastContact(row.original)}</span>;
			},
		},
		{
			id: "deal",
			accessorFn: (row) => row.deals[0]?.name || "",
			header: () => <span>Deal</span>,
			size: 130,
			cell: ({ row }) => assocCell(row.original.deals),
		},
		{
			id: "company",
			accessorFn: (row) => row.companies[0]?.name || "",
			header: () => <span>Company</span>,
			size: 130,
			cell: ({ row }) => assocCell(row.original.companies),
		},
		{
			id: "entity",
			accessorFn: (row) => row.entities[0]?.name || "",
			header: () => <span>Entity</span>,
			size: 130,
			cell: ({ row }) => assocCell(row.original.entities),
		},
		{
			id: "property",
			accessorFn: (row) => row.properties[0]?.name || "",
			header: () => <span>Property</span>,
			size: 130,
			cell: ({ row }) => assocCell(row.original.properties),
		},
		{
			id: "unit",
			accessorFn: (row) => row.units[0]?.name || "",
			header: () => <span>Unit</span>,
			size: 110,
			cell: ({ row }) => assocCell(row.original.units),
		},
		{
			accessorKey: "ignore",
			header: "",
			size: 40,
			enableSorting: false,
			cell: ({ row }) => {
				const c = row.original;
				if (ignoreSaving[c.id]) return <CircularProgress size={16} sx={{ ml: 0.75 }} />;
				const Icon = c.ignored ? VisibilityIcon : VisibilityOffIcon;
				return (
					<Tooltip title={c.ignored ? "Restore to active" : "Ignore contact"}>
						<IconButton
							size="small"
							onClick={(e) => { e.stopPropagation(); handleToggleIgnored(c); }}
						>
							<Icon sx={{ fontSize: 17, color: "#9ca3af" }} />
						</IconButton>
					</Tooltip>
				);
			},
		},
		{
			accessorKey: "activity",
			header: "",
			size: 40,
			enableSorting: false,
			cell: ({ row }) => (
				<Tooltip title="View activity">
					<IconButton
						size="small"
						onClick={(e) => { e.stopPropagation(); setActivityContact(row.original); }}
					>
						<ReceiptIcon sx={{ fontSize: 17, color: "#9ca3af" }} />
					</IconButton>
				</Tooltip>
			),
		},
		{
			accessorKey: "hubspotLink",
			header: "",
			size: 40,
			enableSorting: false,
			cell: ({ row }) => {
				const { hubspotUrl } = row.original;
				if (!hubspotUrl) return null;
				return (
					<Tooltip title="Open in HubSpot">
						<IconButton
							size="small"
							component="a"
							href={hubspotUrl}
							target="_blank"
							rel="noreferrer"
							onClick={(e) => e.stopPropagation()}
						>
							<OpenInNewIcon sx={{ fontSize: 17, color: "#9ca3af" }} />
						</IconButton>
					</Tooltip>
				);
			},
		},
		{
			accessorKey: "action",
			header: "",
			size: 44,
			enableSorting: false,
			cell: ({ row }) => {
				const c = row.original;
				const needs = needsAttention(c);
				return (
					<Tooltip title="Edit">
						<IconButton
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								originalAssociationsRef.current = ASSOC_GROUPS.reduce(
									(acc, group) => ({ ...acc, [group.key]: c[group.key] }),
									{},
								);
								setOpenId(c.id);
								setSavedNote("");
								setSaveError("");
							}}
						>
							<EditIcon sx={{ fontSize: 18, color: needs ? "#184c93" : "#9ca3af" }} />
						</IconButton>
					</Tooltip>
				);
			},
		},
	// handleToggleIgnored is recreated each render; ignoreSaving/loggedInId are
	// the only values it (and the ignore cell) read that can change.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	], [ignoreSaving, loggedInId]);

	return (
		<div className="ContactsCleanupTab">
			{loading ? (
				<ContactsSkeleton />
			) : (
				<>
					<div className="ccToolbar">
						<TextField
							size="small"
							placeholder="Search name, phone, company…"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							sx={{ width: 320, background: "#fff" }}
							InputProps={{
								startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} /></InputAdornment>,
							}}
						/>
						<div className="ccChips">
							{FILTER_CHIPS.map((chip) => (
								<div
									key={chip.key}
									className={`ccChip${chip.key === "attention" ? " ccChip--attention" : ""}${filter === chip.key ? " ccChip--active" : ""}`}
									onClick={() => setFilter(chip.key)}
								>
									{chip.label} {chip.key === "attention" ? `(${attentionCount})` : chip.key === "all" ? `(${viewContacts.length})` : ""}
								</div>
							))}
							<div className="ccViewToggle">
								{VIEW_TOGGLES.map((v) => (
									<div
										key={v.key}
										className={`ccViewToggle-option${view === v.key ? " ccViewToggle-option--active" : ""}`}
										onClick={() => setView(v.key)}
									>
										{v.label} ({v.key === "ignored" ? ignoredCount : contacts.length - ignoredCount})
									</div>
								))}
							</div>
						</div>
					</div>

					{ignoreError && (
						<Typography variant="caption" color="error" sx={{ display: "block" }}>
							{ignoreError}
						</Typography>
					)}

					<div className="ccTableWrap">
						{loadError ? (
							<div className="ccStateMessage ccStateMessage--error">{loadError}</div>
						) : (
							<TanStackTable
								columnData={columns}
								rowData={visibleContacts}
								showSearch={false}
								getRowClassName={(c) => (needsAttention(c) ? "ccRow--attention" : "")}
							/>
						)}
					</div>
				</>
			)}

			<Dialog open={!!openContact} onClose={() => setOpenId(null)} maxWidth="md" fullWidth>
				{openContact && (
					<ContactEditPanel
						contact={openContact}
						onPatch={(patch) => patchContact(openContact.id, patch)}
						onClose={() => setOpenId(null)}
						onSave={() => handleSaveContact(openContact)}
						saving={saving}
						saveError={saveError}
						savedNote={savedNote}
					/>
				)}
			</Dialog>

			<Dialog open={!!activityContact} onClose={() => setActivityContact(null)} maxWidth="md" fullWidth>
				{activityContact && (
					<ActivityPanel
						contact={activityContact}
						activity={activity}
						loading={activityLoading}
						error={activityError}
						onClose={() => setActivityContact(null)}
						onDealsChange={handleActivityDealsChange}
						dealSaving={activityDealSaving}
						dealError={activityDealError}
					/>
				)}
			</Dialog>
		</div>
	);
}

// ── Edit panel ───────────────────────────────────────────────────────────────

function ContactEditPanel({ contact: c, onPatch, onClose, onSave, saving, saveError, savedNote }) {
	return (
		<>
			<DialogTitle className="ccEditDialogTitle">
				{displayName(c)}
				<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
					HubSpot Contact · {c.ownedByMe ? "Owned by you" : "On one of your deals"}
				</Typography>
			</DialogTitle>
			<DialogContent className="ccEditDialogContent">
				<div className="ccPanel-body">
					<div className="dialogSubtitle">Identity</div>
					<div className="FormBlock">
						<div className="FormRow">
							<TextField sx={{ flex: 1 }} label="First Name" value={c.first} placeholder="Add first name" onChange={(e) => onPatch({ first: e.target.value })} />
							<TextField sx={{ flex: 1 }} label="Last Name" value={c.last} placeholder="Add last name" onChange={(e) => onPatch({ last: e.target.value })} />
						</div>
						<div className="FormRow">
							<TextField sx={{ flex: 1 }} label="Email" type="email" value={c.email} placeholder="name@company.com" onChange={(e) => onPatch({ email: e.target.value })} />
						</div>
					</div>

					<div className="dialogSubtitle">Phone Numbers</div>
					<div className="FormBlock">
						<div className="FormRow">
							<TextField sx={{ flex: 1 }} label="Phone Number" value={c.phone} placeholder="(555) 555-0100" onChange={(e) => onPatch({ phone: e.target.value })} />
							<TextField sx={{ flex: 1 }} label="Mobile Number" value={c.mobilePhone} placeholder="(555) 555-0100" onChange={(e) => onPatch({ mobilePhone: e.target.value })} />
						</div>
					</div>

					<div className="dialogSubtitle">Associations</div>
					<div className="FormBlock">
						{ASSOC_GROUPS.map((group) => (
							<div className="FormRow" key={group.key}>
								<AssociationField
									groupKey={group.key}
									label={group.label}
									placeholder={group.placeholder}
									value={c[group.key]}
									onChange={(newValue) => onPatch({ [group.key]: newValue })}
								/>
							</div>
						))}
					</div>
				</div>

				{savedNote && (
					<Typography variant="caption" color="success.main" sx={{ display: "block" }}>
						{savedNote}
					</Typography>
				)}
				{saveError && (
					<Typography variant="caption" color="error" sx={{ display: "block" }}>
						{saveError}
					</Typography>
				)}

				<div className="dialogButtons">
					<Button variant="outlined" color="primary" onClick={onClose} disabled={saving}>Cancel</Button>
					<Button
						variant="contained"
						color="primary"
						disableElevation
						onClick={onSave}
						disabled={saving}
						startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
					>
						{saving ? "Saving…" : "Save Contact"}
					</Button>
				</div>
			</DialogContent>
		</>
	);
}

// ── Association field (live HubSpot search-as-you-type) ─────────────────────

function AssociationField({ groupKey, label, placeholder, value, onChange, disabled }) {
	const { instance, accounts } = useMsal();
	const [inputValue, setInputValue] = useState("");
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);

	const search = useMemo(() => debounce(async (query, inst, accts) => {
		if (query.trim().length < 2) { setOptions([]); return; }
		setLoading(true);
		try {
			const res = await getAPICall(inst, accts[0], `/api/hubspot/search/associationOptions?groupKey=${groupKey}&query=${encodeURIComponent(query)}`);
			setOptions(Array.isArray(res.data) ? res.data : []);
		} catch (err) {
			console.error(`[AssociationField:${groupKey}] search failed`, err);
			setOptions([]);
		} finally {
			setLoading(false);
		}
	}, 400), [groupKey]);

	return (
		<Autocomplete
			sx={{ flex: 1, minWidth: 0 }}
			multiple
			limitTags={2}
			filterSelectedOptions
			disabled={disabled}
			value={value}
			inputValue={inputValue}
			onInputChange={(_, newInput, reason) => {
				setInputValue(newInput);
				if (reason !== "reset") search(newInput, instance, accounts);
			}}
			onChange={(_, newValue) => onChange(newValue)}
			options={options}
			getOptionLabel={(o) => o.name || ""}
			isOptionEqualToValue={(o, v) => o.id === v.id}
			loading={loading}
			noOptionsText={inputValue.trim().length < 2 ? "Type to search…" : "No matches"}
			renderInput={(params) => (
				<TextField
					{...params}
					label={label}
					placeholder={placeholder}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<>
								{loading ? <CircularProgress size={16} sx={{ mr: 1 }} /> : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
}

// ── Activity panel ───────────────────────────────────────────────────────────

function formatActivityDate(iso) {
	if (!iso) return "";
	const d = new Date(iso);
	if (Number.isNaN(d.getTime())) return "";
	return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

function ActivityPanel({ contact: c, activity, loading, error, onClose, onDealsChange, dealSaving, dealError }) {
	const { instance, accounts } = useMsal();
	const [listeningCallId, setListeningCallId] = useState(null);
	const [audioBlobUrl, setAudioBlobUrl] = useState(null);
	// Which activity rows' deal picker is expanded, keyed by `${type}:${id}` —
	// keeps the multiselect hidden (and names shown as plain text) until the
	// user deliberately opts into editing, so a stray click can't reassociate
	// a deal by accident.
	const [editingDeals, setEditingDeals] = useState({});
	// In-progress edits, keyed the same way — nothing is sent to HubSpot until
	// Save is clicked, so picking/removing deals in the multiselect only
	// touches this local draft, not the row's committed `deals` array.
	const [draftDeals, setDraftDeals] = useState({});

	const startEditingDeals = (item, key) => {
		setDraftDeals((d) => ({ ...d, [key]: item.deals || [] }));
		setEditingDeals((s) => ({ ...s, [key]: true }));
	};

	const cancelEditingDeals = (key) => {
		setEditingDeals((s) => ({ ...s, [key]: false }));
		setDraftDeals((d) => {
			const next = { ...d };
			delete next[key];
			return next;
		});
	};

	const saveEditingDeals = async (item, key) => {
		const newDeals = draftDeals[key] ?? item.deals ?? [];
		const success = await onDealsChange(item, newDeals);
		if (!success) return;
		setEditingDeals((s) => ({ ...s, [key]: false }));
		setDraftDeals((d) => {
			const next = { ...d };
			delete next[key];
			return next;
		});
	};

	// Same pattern as ProjectViewer's contact call list: the recording endpoint
	// needs an MSAL bearer token, so a plain <audio src="/api/..."> can't hit it
	// directly — fetch it as a blob and play from an object URL instead.
	useEffect(() => {
		if (!listeningCallId) return;
		let cancelled = false;
		let localBlobUrl = null;
		setAudioBlobUrl(null);
		getBlobAPICall(instance, accounts[0], "/api/hubspot/callRecording", { engagementId: listeningCallId })
			.then((blob) => {
				if (cancelled) return;
				localBlobUrl = URL.createObjectURL(blob);
				setAudioBlobUrl(localBlobUrl);
			})
			.catch((err) => console.error("[ActivityPanel] callRecording failed to load:", err?.message));
		return () => {
			cancelled = true;
			if (localBlobUrl) URL.revokeObjectURL(localBlobUrl);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [listeningCallId]);

	const toggleListen = (callId) => setListeningCallId((cur) => (cur === callId ? null : callId));

	return (
		<>
			<DialogTitle className="ccEditDialogTitle">
				{displayName(c)}
				<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", fontWeight: 400, mt: 0.25 }}>
					Recent activity
				</Typography>
			</DialogTitle>
			<DialogContent className="ccEditDialogContent">
				{loading ? (
					<div className="ccStateMessage"><CircularProgress size={22} /></div>
				) : error ? (
					<div className="ccStateMessage ccStateMessage--error">{error}</div>
				) : !activity.length ? (
					<div className="ccStateMessage">No recent calls or emails found for this contact.</div>
				) : (
					<div className="ccActivityList">
						{activity.map((a, i) => {
							const listening = a.type === "call" && listeningCallId === a.callId;
							const dealKey = `${a.type}:${a.id}`;
							return (
								<div className="ccActivityRow" key={i}>
									{a.type === "call" ? <CallIcon sx={{ fontSize: 18, color: "#9ca3af" }} /> : <EmailIcon sx={{ fontSize: 18, color: "#9ca3af" }} />}
									<div className="ccActivityRow-body">
										<div className="ccActivityRow-header">
											<span className="ccActivityRow-title">{a.title}</span>
											<span className="ccMuted">{formatActivityDate(a.date)}</span>
										</div>
										<div className="ccMuted">
											{a.direction && `${a.direction.toLowerCase()} · `}
											{a.type === "call" && a.duration ? `${a.duration} · ` : ""}
											{a.ownerName || "Unknown owner"}
										</div>
										{a.summary && <div className="ccActivityRow-summary">{a.summary}</div>}
										<div className="ccActivityRow-links">
											{a.type === "call" && a.hasRecording && (
												<Tooltip title={listening ? "Close recording" : "Play recording"}>
													<IconButton size="small" color={listening ? "primary" : "default"} onClick={() => toggleListen(a.callId)}>
														<PlayCircleIcon sx={{ fontSize: 20 }} />
													</IconButton>
												</Tooltip>
											)}
											{a.url && (
												<a className="ccLink" href={a.url} target="_blank" rel="noreferrer">View in HubSpot</a>
											)}
										</div>
										{listening && (
											audioBlobUrl
												? <audio controls autoPlay style={{ width: "100%", height: 36 }} src={audioBlobUrl} />
												: <span className="ccMuted">Loading recording…</span>
										)}
										<div className={`ccActivityRow-deals${editingDeals[dealKey] ? " ccActivityRow-deals--editing" : ""}`}>
											{editingDeals[dealKey] ? (
												<>
													<AssociationField
														groupKey="deals"
														label="Deals"
														placeholder="Search deals…"
														value={draftDeals[dealKey] ?? a.deals ?? []}
														onChange={(newDeals) => setDraftDeals((d) => ({ ...d, [dealKey]: newDeals }))}
														disabled={!!dealSaving?.[dealKey]}
													/>
													{dealSaving?.[dealKey] && <CircularProgress size={14} sx={{ ml: 1 }} />}
													<Tooltip title="Save">
														<IconButton size="small" disabled={!!dealSaving?.[dealKey]} onClick={() => saveEditingDeals(a, dealKey)}>
															<CheckIcon sx={{ fontSize: 18, color: "#184c93" }} />
														</IconButton>
													</Tooltip>
													<Tooltip title="Cancel">
														<IconButton size="small" disabled={!!dealSaving?.[dealKey]} onClick={() => cancelEditingDeals(dealKey)}>
															<CloseIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
														</IconButton>
													</Tooltip>
												</>
											) : (a.deals || []).length ? (
												<>
													<span className="ccActivityRow-dealNames">
														<span className="ccActivityRow-dealsLabel">Deals: </span>
														{a.deals.map((d) => d.name).join(", ")}
													</span>
													<Tooltip title="Edit deal associations">
														<IconButton size="small" onClick={() => startEditingDeals(a, dealKey)}>
															<EditIcon sx={{ fontSize: 15, color: "#184c93" }} />
														</IconButton>
													</Tooltip>
												</>
											) : (
												<span
													className="ccActivityRow-dealsAdd"
													role="button"
													tabIndex={0}
													onClick={() => startEditingDeals(a, dealKey)}
													onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") startEditingDeals(a, dealKey); }}
												>
													<AddIcon sx={{ fontSize: 15 }} /> Associate Deals
												</span>
											)}
										</div>
										{dealError?.[dealKey] && (
											<Typography variant="caption" color="error" sx={{ display: "block" }}>
												{dealError[dealKey]}
											</Typography>
										)}
									</div>
								</div>
							);
						})}
					</div>
				)}

				<div className="dialogButtons">
					<Button variant="outlined" color="primary" onClick={onClose}>Close</Button>
				</div>
			</DialogContent>
		</>
	);
}
